import React from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Select,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { ColumnInfo, Header } from "build-ts/metadata";
import { DataType, dataTypeToJSON } from "build-ts/enums";
import { MdBarChart, MdFilterList } from "react-icons/md";
import { BiSolidNetworkChart } from "react-icons/bi";

export type UnmappedSortOption = "default" | "name-asc" | "name-desc";

const UNMAPPED_SORT_LABEL: Record<UnmappedSortOption, string> = {
  default: "Sort",
  "name-asc": "A→Z",
  "name-desc": "Z→A",
};

const MAP_PANEL_SCROLL_SX = {
  scrollbarWidth: "thin",
  scrollbarColor: "#CBD5E0 transparent",
  "&::-webkit-scrollbar": { width: "6px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { backgroundColor: "#CBD5E0", borderRadius: "6px" },
  "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#A0AEC0" },
};

interface OntologyMappingLeftPanelProps {
  leftPanelCount: number;
  unmappedDisplayEntries: { column: ColumnInfo; index: number }[];
  unmappedSort: UnmappedSortOption;
  setUnmappedSort: (sort: UnmappedSortOption) => void;
  unmappedSearchQuery: string;
  setUnmappedSearchQuery: (q: string) => void;
  addedColumnIds: Set<string>;
  updatedColumnIds: Set<string>;
  pendingPromoteIds: Set<string>;
  getCombinedMetrics: (columnId: string) => Header[];
  handlePromoteFromLeft: (column: ColumnInfo, index: number) => void;
  handleColumnChange: (
    index: number,
    property: "data_type" | "column_type" | "default_aggregation",
    value: string
  ) => void;
}

const OntologyMappingLeftPanel: React.FC<OntologyMappingLeftPanelProps> = ({
  leftPanelCount,
  unmappedDisplayEntries,
  unmappedSort,
  setUnmappedSort,
  unmappedSearchQuery,
  setUnmappedSearchQuery,
  addedColumnIds,
  updatedColumnIds,
  pendingPromoteIds,
  getCombinedMetrics,
  handlePromoteFromLeft,
  handleColumnChange,
}) => {
  const dataTypeSelect = (col: ColumnInfo, index: number) => (
    <Select
      size="xs"
      w="fit-content"
      maxW="140px"
      value={dataTypeToJSON(col.data_type!) || ""}
      onChange={e => handleColumnChange(index, "data_type", e.target.value)}
      bg="white"
      borderRadius="md"
      fontWeight="600"
      fontSize="11px"
      borderColor="gray.300"
    >
      <option value={dataTypeToJSON(DataType.VARCHAR)}>VARCHAR</option>
      <option value={dataTypeToJSON(DataType.BOOLEAN)}>BOOLEAN</option>
      <option value={dataTypeToJSON(DataType.INT32)}>INT32</option>
      <option value={dataTypeToJSON(DataType.INT64)}>INT64</option>
      <option value={dataTypeToJSON(DataType.DOUBLE)}>DOUBLE</option>
      <option value={dataTypeToJSON(DataType.DATE)}>DATE</option>
      <option value={dataTypeToJSON(DataType.DATETIME)}>DATETIME</option>
    </Select>
  );

  return (
    <Flex
      flex={{ base: "1", md: "0 0 30%" }}
      w={{ base: "100%", md: "30%" }}
      maxW={{ md: "360px" }}
      minW={{ md: 0 }}
      direction="column"
      borderRightWidth={{ md: "1px" }}
      borderBottomWidth={{ base: "1px", md: 0 }}
      borderColor="gray.400"
      pr={{ md: 3 }}
      pb={{ base: 3, md: 0 }}
    >
      <VStack align="stretch" spacing={2} mb={2} flexShrink={0} w="100%">
        <Flex align="center" justify="space-between" gap={2} flexWrap="wrap" w="100%">
          <Text fontSize="sm" fontWeight="800" color="gray.700" mb={0} flexShrink={0}>
            Unmapped ({leftPanelCount})
          </Text>
          <Menu placement="bottom-end" closeOnSelect>
            <MenuButton
              as={Button}
              size="xs"
              variant={unmappedSort === "default" ? "outline" : "solid"}
              colorScheme={unmappedSort === "default" ? "gray" : "purple"}
              flexShrink={0}
              fontWeight="600"
              leftIcon={<Icon as={MdFilterList} boxSize={3.5} aria-hidden />}
              rightIcon={<ChevronDownIcon boxSize={3} />}
            >
              {UNMAPPED_SORT_LABEL[unmappedSort]}
            </MenuButton>
            <MenuList fontSize="sm" minW="200px" zIndex={30}>
              <MenuItem
                fontWeight={unmappedSort === "default" ? "700" : "400"}
                icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                onClick={() => setUnmappedSort("default")}
              >
                Default order
              </MenuItem>
              <MenuItem
                fontWeight={unmappedSort === "name-asc" ? "700" : "400"}
                icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                onClick={() => setUnmappedSort(unmappedSort === "name-asc" ? "default" : "name-asc")}
              >
                Name A→Z
              </MenuItem>
              <MenuItem
                fontWeight={unmappedSort === "name-desc" ? "700" : "400"}
                icon={<Icon as={MdFilterList} boxSize={4} color="gray.500" />}
                onClick={() => setUnmappedSort(unmappedSort === "name-desc" ? "default" : "name-desc")}
              >
                Name Z→A
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
        <Input
          size="sm"
          w="100%"
          placeholder="Search columns..."
          value={unmappedSearchQuery}
          onChange={e => setUnmappedSearchQuery(e.target.value)}
          bg="gray.50"
          borderRadius="md"
        />
      </VStack>

      <Box
        flex={1}
        minH={0}
        overflowY="auto"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="md"
        sx={MAP_PANEL_SCROLL_SX}
      >
        {leftPanelCount === 0 ? (
          <Box py={8} px={3} textAlign="center">
            <Text color="gray.500" fontSize="sm" mb={0}>
              No unmapped columns.
            </Text>
          </Box>
        ) : unmappedDisplayEntries.length === 0 ? (
          <Box py={8} px={3} textAlign="center">
            <Text color="gray.500" fontSize="sm" mb={0}>
              No columns match your search.
            </Text>
          </Box>
        ) : (
          unmappedDisplayEntries.map(({ column, index }) => {
            const id = column.header?.id;
            const isNew = Boolean(id && addedColumnIds.has(id));
            const isUpdated = Boolean(id && updatedColumnIds.has(id));
            const awaitingOntologyOnRight = Boolean(id && pendingPromoteIds.has(id));
            const columnMetrics = id ? getCombinedMetrics(id) : [];
            const hasMetrics = columnMetrics.length > 0;

            return (
              <Box
                key={`left-${id ?? index}`}
                position="relative"
                w="100%"
                borderBottomWidth="1px"
                borderColor="gray.100"
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handlePromoteFromLeft(column, index);
                  }
                }}
                _focusVisible={{
                  outline: "2px solid",
                  outlineColor: "purple.500",
                  outlineOffset: 2,
                }}
              >
                {/* Full-row click target; interactive children use pointer-events: auto */}
                <Box
                  position="absolute"
                  inset={0}
                  zIndex={0}
                  cursor="pointer"
                  aria-hidden
                  _hover={{ bg: awaitingOntologyOnRight ? "orange.100" : "gray.50" }}
                  bg={awaitingOntologyOnRight ? "orange.100" : "transparent"}
                  onClick={() => handlePromoteFromLeft(column, index)}
                />
                <HStack
                  position="relative"
                  zIndex={1}
                  justify="space-between"
                  align="flex-start"
                  spacing={2}
                  px={3}
                  py={3}
                  pointerEvents="none"
                >
                  <VStack align="start" flex={1} minW={0} spacing={2}>
                    <HStack spacing={2} align="center" flexWrap="wrap">
                      <Text fontWeight="700" color="gray.900" mb={0} fontSize="sm" textAlign="left">
                        {column.header?.name || ""}
                      </Text>
                      {isNew && (
                        <Badge colorScheme="blue" fontSize="9px" variant="subtle">
                          new
                        </Badge>
                      )}
                      {isUpdated && (
                        <Badge colorScheme="orange" fontSize="9px" variant="subtle">
                          updated
                        </Badge>
                      )}
                      {hasMetrics && (
                        <Tooltip
                          label={columnMetrics.map(m => m.name).join(", ")}
                          hasArrow
                          bg="gray.700"
                          color="white"
                          fontSize="xs"
                          openDelay={200}
                          placement="top"
                        >
                          <HStack
                            spacing={0.5}
                            as="span"
                            pointerEvents="auto"
                            px={1.5}
                            py={0.5}
                            borderRadius="md"
                            bg="purple.50"
                            borderWidth="1px"
                            borderColor="purple.200"
                          >
                            <Icon as={MdBarChart} boxSize={3} color="purple.600" />
                            <Text mb={0} fontSize="9px" fontWeight="700" color="purple.600" lineHeight="1">
                              {columnMetrics.length}
                            </Text>
                          </HStack>
                        </Tooltip>
                      )}
                    </HStack>
                    <Box
                      pointerEvents="auto"
                      onClick={e => e.stopPropagation()}
                      onMouseDown={e => e.stopPropagation()}
                      onPointerDown={e => e.stopPropagation()}
                    >
                      {dataTypeSelect(column, index)}
                    </Box>
                  </VStack>
                  <Tooltip
                    label={
                      awaitingOntologyOnRight
                        ? "Finish ontology mapping in the Mapped panel →"
                        : "Click to map this column"
                    }
                    hasArrow
                    openDelay={400}
                    placement="left"
                  >
                    <Box
                      flexShrink={0}
                      pt={0.5}
                      pointerEvents="auto"
                      cursor="pointer"
                      aria-hidden
                      onClick={e => {
                        e.stopPropagation();
                        handlePromoteFromLeft(column, index);
                      }}
                      onMouseDown={e => e.stopPropagation()}
                    >
                      {awaitingOntologyOnRight ? (
                        <Icon as={BiSolidNetworkChart} color="orange.700" boxSize={7} />
                      ) : (
                        <Icon as={ChevronRightIcon} color="gray.400" boxSize={5} />
                      )}
                    </Box>
                  </Tooltip>
                </HStack>
              </Box>
            );
          })
        )}
      </Box>
    </Flex>
  );
};

export default OntologyMappingLeftPanel;
